const OEM_BRANDS = Object.freeze({
  DEFAULT: 'default',
  JINDOUYUN: 'jindouyun',
  DAPEN: 'dapen',
  PIKE: 'pike'
})

const OEM_HOSTNAMES = Object.freeze({
  'canvas-oem-jindouyun.muchcloud.com': OEM_BRANDS.JINDOUYUN,
  'aiopcai.cn': OEM_BRANDS.JINDOUYUN,
  'canvas-oem-dapen.muchcloud.com': OEM_BRANDS.DAPEN,
  'mp.dapenai.com': OEM_BRANDS.DAPEN,
  'canvas-oem-picit.muchcloud.com': OEM_BRANDS.PIKE,
  'pro.picit.art': OEM_BRANDS.PIKE
})

const OEM_STAGING_HOSTNAMES = Object.freeze([
  'canvas-oem-jindouyun.muchcloud.com',
  'canvas-oem-dapen.muchcloud.com',
  'canvas-oem-picit.muchcloud.com'
])

const DEFAULT_FEATURES = Object.freeze({
  hideBannerStrip: false,
  hideWorksGallery: false,
  hideInvite: false,
  hideFiling: false,
  hideEditorShareActions: false,
  hideDocsEntry: false,
  hideApiEntry: false,
  hideWishlistEntry: false,
  hideCreditRecords: false,
  hideRecharge: false
})

const RESTRICTED_OEM_FEATURES = Object.freeze({
  ...DEFAULT_FEATURES,
  hideBannerStrip: true,
  hideWorksGallery: true,
  hideInvite: true,
  hideFiling: true,
  hideEditorShareActions: true,
  hideDocsEntry: true,
  hideApiEntry: true,
  hideWishlistEntry: true
})

const OEM_CONFIGS = Object.freeze({
  [OEM_BRANDS.DEFAULT]: {
    brand: OEM_BRANDS.DEFAULT,
    brandName: 'DreamoTV',
    chineseBrandName: '绘梦工坊',
    reportName: '绘梦工坊',
    appTitle: 'DreamoTV - 无限空间 自由创作',
    favicon: '/favicon.ico',
    headerLogo: '',
    headerText: '',
    workflowHomeUrl: 'https://huimengi.com',
    features: DEFAULT_FEATURES
  },
  [OEM_BRANDS.JINDOUYUN]: {
    brand: OEM_BRANDS.JINDOUYUN,
    brandName: '金斗云画布',
    chineseBrandName: '金斗云画布',
    reportName: '金斗云画布',
    appTitle: '金斗云画布 - 无限空间 自由创作',
    favicon: '/favicon-jdy.ico',
    headerLogo: '/logo-jdy.png',
    headerText: '金斗云画布',
    workflowHomeUrl: 'https://aiopcai.com',
    developmentLoginUrl: 'https://jdy-oem.yunxiacn.com/login',
    features: {
      ...RESTRICTED_OEM_FEATURES,
      hideCreditRecords: true,
      hideRecharge: true
    }
  },
  [OEM_BRANDS.DAPEN]: {
    brand: OEM_BRANDS.DAPEN,
    brandName: '大盆文化',
    chineseBrandName: '大盆文化',
    reportName: '大盆文化',
    appTitle: '大盆文化 - 无限空间 自由创作',
    favicon: '/favicon-dp.ico',
    headerLogo: '/dapen-dark-horizontal.png',
    headerText: '',
    // workflowHomeUrl: 'http://192.168.104.124:3001',
    workflowHomeUrl: 'https://tool.dapenai.com',
    developmentLoginUrl: 'https://dapen-oem.yunxiacn.com/login',
    features: RESTRICTED_OEM_FEATURES
  },
  [OEM_BRANDS.PIKE]: {
    brand: OEM_BRANDS.PIKE,
    brandName: '皮克漫剧',
    chineseBrandName: '皮克漫剧',
    reportName: '皮克漫剧',
    appTitle: '皮克漫剧 - 无限空间 自由创作',
    favicon: '/favicon-pike.ico',
    headerLogo: '/logo-pike.png',
    headerText: '',
    workflowHomeUrl: 'https://mj.picit.art',
    developmentLoginUrl: 'https://picit-oem.yunxiacn.com/login',
    features: RESTRICTED_OEM_FEATURES
  }
})

const ICON_REL_VALUES = ['icon', 'shortcut icon']

const normalizeOemBrand = (value) => {
  const brand = String(value || '')
    .trim()
    .toLowerCase()
  return OEM_CONFIGS[brand] ? brand : OEM_BRANDS.DEFAULT
}

const normalizeOemHostname = (hostname) =>
  String(hostname || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '')

export const resolveOemBrandFromHostname = (hostname) =>
  OEM_HOSTNAMES[normalizeOemHostname(hostname)] || OEM_BRANDS.DEFAULT

export const isOemStagingHostname = (hostname) =>
  OEM_STAGING_HOSTNAMES.includes(normalizeOemHostname(hostname))

export const resolveOemConfigFromHostname = (hostname) =>
  OEM_CONFIGS[resolveOemBrandFromHostname(hostname)]

export const resolveOemFaviconFromHostname = (hostname) =>
  resolveOemConfigFromHostname(hostname).favicon

const resolveRuntimeOemBrand = () => {
  if (typeof window === 'undefined') {
    return OEM_BRANDS.DEFAULT
  }

  return resolveOemBrandFromHostname(window.location?.hostname)
}

export const CURRENT_OEM_BRAND = normalizeOemBrand(
  import.meta.env?.VITE_OEM_BRAND || resolveRuntimeOemBrand()
)

export const CURRENT_OEM_CONFIG = OEM_CONFIGS[CURRENT_OEM_BRAND]
export const CURRENT_OEM_FEATURES = CURRENT_OEM_CONFIG.features
export const IS_OEM = CURRENT_OEM_BRAND !== OEM_BRANDS.DEFAULT
export const OEM_REPORT_NAME = CURRENT_OEM_CONFIG.reportName

export const getCurrentOemConfig = () => CURRENT_OEM_CONFIG

export const getCurrentOemFeatures = () => CURRENT_OEM_FEATURES

export const getOemAppTitle = () => CURRENT_OEM_CONFIG.appTitle

export const getOemChineseBrandName = () => CURRENT_OEM_CONFIG.chineseBrandName

export const getOemMessageIconUrl = (fallbackUrl = '') => {
  if (IS_OEM && CURRENT_OEM_CONFIG.favicon) {
    return CURRENT_OEM_CONFIG.favicon
  }
  return fallbackUrl
}

export const resolveOemWorkflowHomeUrl = (fallbackUrl = '') =>
  CURRENT_OEM_CONFIG.workflowHomeUrl || fallbackUrl

const trimTrailingSlash = (value = '') =>
  String(value || '')
    .trim()
    .replace(/\/+$/, '')

const joinOemPath = (base, path) =>
  `${trimTrailingSlash(base)}/${String(path || '').replace(/^\//, '')}`

// 测试部署使用 vite build --mode development；此时 DEV 仍为 false。
// staging OEM 使用各品牌的测试登录域，不能跳品牌正式登录域。
const isDevelopmentBuild = import.meta.env?.MODE === 'development'

/**
 * 外部登录地址。
 * - staging / development 构建：优先使用品牌测试登录地址，主站使用环境变量
 * - 生产构建 OEM：用 workflowHomeUrl/login，避免仍跳转绘梦默认域
 * - 主站：用环境变量
 */
export const resolveOemExternalLoginUrl = (envFallback = '') => {
  const envLoginUrl = String(envFallback || '').trim()
  if (isDevelopmentBuild) {
    const developmentLoginUrl =
      CURRENT_OEM_CONFIG.developmentLoginUrl || envLoginUrl
    if (developmentLoginUrl) return developmentLoginUrl
  }

  if (IS_OEM) {
    const workflowBase = trimTrailingSlash(CURRENT_OEM_CONFIG.workflowHomeUrl)
    if (workflowBase) {
      return joinOemPath(workflowBase, 'login')
    }
  }

  return envLoginUrl
}

/**
 * 聚合平台根地址（充值/积分明细等 iframe）。
 * - staging OEM：与测试登录地址同源
 * - 生产 OEM：用 platformBaseUrl 或 workflowHomeUrl
 * - 主站：用构建环境变量
 */
export const resolveOemPlatformBaseUrl = (envFallback = '') => {
  if (IS_OEM && isDevelopmentBuild) {
    const loginUrl = resolveOemExternalLoginUrl(
      import.meta.env?.VITE_EXTERNAL_LOGIN_URL || envFallback
    )
    try {
      return new URL(loginUrl).origin
    } catch {
      // 登录地址不是绝对 URL 时，沿用品牌平台配置。
    }
  }

  const configured = trimTrailingSlash(CURRENT_OEM_CONFIG.platformBaseUrl)
  if (configured) {
    return configured
  }

  if (IS_OEM) {
    const workflowBase = trimTrailingSlash(CURRENT_OEM_CONFIG.workflowHomeUrl)
    if (workflowBase) {
      return workflowBase
    }
  }

  return trimTrailingSlash(envFallback)
}

const CHINESE_DREAMO_BRAND_PATTERN = /绘梦工坊|绘梦/g

export const stripChineseDreamoBrandText = (value) => {
  const text = String(value ?? '')
  if (!IS_OEM || !text) return text
  return text.replace(CHINESE_DREAMO_BRAND_PATTERN, '')
}

export const replaceChineseDreamoBrandText = (value) => {
  const text = String(value || '')
  if (!IS_OEM) return text

  return text
    .replaceAll('绘梦工坊', CURRENT_OEM_CONFIG.chineseBrandName)
    .replaceAll('绘梦', CURRENT_OEM_CONFIG.chineseBrandName)
}

export const buildOemDocumentChromeInlineScript = () => {
  const hostChrome = Object.fromEntries(
    Object.entries(OEM_HOSTNAMES).map(([hostname, brand]) => {
      const config = OEM_CONFIGS[brand]
      return [
        hostname,
        {
          favicon: config.favicon,
          title: config.appTitle
        }
      ]
    })
  )
  const fallback = {
    favicon: OEM_CONFIGS[OEM_BRANDS.DEFAULT].favicon,
    title: OEM_CONFIGS[OEM_BRANDS.DEFAULT].appTitle
  }

  return `(function(){var host=String(location.hostname||'').replace(/^www\\./,'').toLowerCase();var chrome=(${JSON.stringify(
    hostChrome
  )})[host]||${JSON.stringify(
    fallback
  )};document.write('<link rel="icon" type="image/x-icon" href="'+chrome.favicon+'">');document.write('<link rel="shortcut icon" href="'+chrome.favicon+'">');document.write('<title>'+chrome.title+'</title>');})();`
}

const upsertIconLink = (rel, href) => {
  const links = document.querySelectorAll(
    'link[rel="icon"], link[rel="shortcut icon"]'
  )
  const match = Array.from(links).find(
    (link) => link.getAttribute('rel') === rel
  )
  const icon = match || document.createElement('link')
  if (!match) {
    icon.setAttribute('rel', rel)
    document.head.appendChild(icon)
  }
  if (icon.getAttribute('href') !== href) {
    icon.setAttribute('type', 'image/x-icon')
    icon.setAttribute('href', href)
  }
}

export const applyOemDocumentChrome = () => {
  if (typeof document === 'undefined') return

  const favicon = CURRENT_OEM_CONFIG.favicon
  if (favicon) {
    ICON_REL_VALUES.forEach((rel) => upsertIconLink(rel, favicon))
  }

  if (IS_OEM) {
    document.title = CURRENT_OEM_CONFIG.appTitle
  }
}

export { OEM_BRANDS, OEM_CONFIGS, OEM_HOSTNAMES }
